import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  isExperienceMessageLevelSchedule,
  isTaskLevelSchedule,
  Schedule,
  ScheduleStatus,
  ScheduleType
} from '@dadohr/shared/domainTypes/schedules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import { flatten, sum } from 'lodash';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import moment from 'moment-timezone';
import { useState } from 'react';
import { AlertBox } from '../../../../../components/AlertBox';
import { ColoredChip } from '../../../../../components/ColoredChip';
import { Loader } from '../../../../../components/Loader';
import { ChannelIcon } from '../../../../../features/Experiences/components/Channels';
import { ParticipantDialog } from '../../../../../features/Experiences/pages/Details/ParticipantDialog';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { usePromise } from '../../../../../hooks/usePromise';
import { FlexContainer, FlexItem } from '../../../../../layout/Flex';
import { Section } from '../../../../../layout/Section';
import {
  getMessageTemplateById,
  getTaskTemplateForTaskId
} from '../../../../../services/experiences/template';
import { updateDoc } from '../../../../../services/firebase';
import { formatDatePrecise, fromMoment } from '../../../../../services/time';
import { toFullName } from '../../../../../services/user';
import { getSchedulesOverviewForAllOrgs } from '../../../../services/schedules';

const OrgItem = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const ExperienceItem = styled('div')((p) => ({
  borderBottom: p.theme.custom.border.standard,
  paddingBottom: p.theme.spacing(3),
  marginBottom: p.theme.spacing(3),

  ':first-of-type': {
    paddingTop: p.theme.spacing(3),
    borderTop: p.theme.custom.border.standard
  }
}));

const ExperienceItemHeaderContainer = styled(FlexContainer)((p) => ({
  marginBottom: p.theme.spacing(),
  cursor: 'pointer'
}));

const ExperienceItemHeading = styled('div')((p) => ({
  fontWeight: p.theme.typography.fontWeightBold
}));

const SchedulesByTypeContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing()
}));

const ScheduleListContainer = styled('div')((p) => ({
  marginLeft: p.theme.spacing(2)
}));

const ScheduleTypeChip = ({ type }: { type: ScheduleType }) => {
  switch (type) {
    case ScheduleType.EXPERIENCE_START:
      return (
        <ColoredChip size="medium" color="black" mode="light">
          Experience Start
        </ColoredChip>
      );
    case ScheduleType.EXPERIENCE_MESSAGE:
      return (
        <ColoredChip size="medium" color="purple" mode="light">
          Experience Message
        </ColoredChip>
      );
    case ScheduleType.TASK_START:
      return (
        <ColoredChip size="medium" color="blue" mode="light">
          Task Start
        </ColoredChip>
      );
    case ScheduleType.TASK_REMINDER:
      return (
        <ColoredChip size="medium" color="yellow" mode="light">
          Task Reminder
        </ColoredChip>
      );
    case ScheduleType.TASK_LATE_REMINDER:
      return (
        <ColoredChip size="medium" color="red" mode="light">
          Task Late Reminder
        </ColoredChip>
      );
    default:
      return null;
  }
};

const ExperienceItemHeader = ({
  tpl,
  exp,
  participant,
  org
}: {
  tpl: Doc<IExperienceTemplate>;
  exp: Doc<IExperience>;
  participant: Doc<IUser>;
  org: Doc<IOrganization>;
}) => {
  const { dialogOpen, closeDialog } = useDialogState(false);
  // TODO - fix current user provider - ParticipantDialog needs a current user
  // which we should be able to provide here, by construction a fictitious one
  // given the org information passed in.
  // Only afterwards the dialog can be opened
  return (
    <>
      <ExperienceItemHeaderContainer role="button" onClick={() => null}>
        <ExperienceItemHeading>
          {toFullName(participant.data)}
        </ExperienceItemHeading>
        <Clock size={12} />
        <Tooltip title="Experience started at">
          <div>{formatDatePrecise(exp.data.startsAt)}</div>
        </Tooltip>
      </ExperienceItemHeaderContainer>
      {dialogOpen && (
        <ParticipantDialog
          d={exp}
          tpl={tpl}
          open={true}
          onClose={closeDialog}
        />
      )}
    </>
  );
};

const SchedulesByType = ({
  tpl,
  exp,
  type,
  ss
}: {
  tpl: Doc<IExperienceTemplate>;
  exp: Doc<IExperience>;
  type: ScheduleType;
  ss: Doc<Schedule>[];
}) => {
  if (type === ScheduleType.TASK_END) {
    return null;
  }
  return (
    <SchedulesByTypeContainer>
      <div>
        <ScheduleTypeChip type={type} />
      </div>
      <ScheduleListContainer>
        {ss.map((s) => {
          const title = isTaskLevelSchedule(s)
            ? getTaskTemplateForTaskId(tpl, exp, s.data.taskId)?.title
            : isExperienceMessageLevelSchedule(s)
            ? getMessageTemplateById(tpl, s.data.msgId)?.subject
            : '';
          return (
            <FlexContainer key={s.id}>
              {(isTaskLevelSchedule(s) ||
                isExperienceMessageLevelSchedule(s)) && (
                <>
                  <ChannelIcon channel={s.data.channel} size={16} />
                  <div>{s.data.actor}</div>
                  <div>|</div>
                  <FlexItem flex={1}>{title}</FlexItem>
                </>
              )}

              <div>{formatDatePrecise(s.data.at)}</div>

              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => {
                  updateDoc(s, () => ({
                    status: ScheduleStatus.SKIPPED
                  }));
                }}
              >
                Skip
              </Button>
            </FlexContainer>
          );
        })}
      </ScheduleListContainer>
    </SchedulesByTypeContainer>
  );
};

export const PageSchedulesOverview = () => {
  const [pointInTime, setPointInTime] = useState(
    moment().add(7, 'h').startOf('h')
  );
  const activeHour = pointInTime.hour();
  const [data, loading, error] = usePromise(async () => {
    // TODO replace with the real deal
    // parametrize to allow to step through time
    const s = moment().add(7, 'h').startOf('h').hour(activeHour);
    return getSchedulesOverviewForAllOrgs({
      start: fromMoment(s),
      end: fromMoment(s.clone().add(1, 'h'))
    });
  }, [activeHour]);

  useErrorLogger('Schedules Overview', error);
  return (
    <Section>
      <AlertBox color="yellow">
        This page shows all open schedules in a given hour for every org. Plain
        schedule items without any batching are shown.
      </AlertBox>
      <FlexContainer fullWidth justifyContent="center" marginBottom={2}>
        <IconButton
          onClick={() => setPointInTime(pointInTime.clone().add(-1, 'h'))}
        >
          <ArrowLeft size={16} />
        </IconButton>
        <div>
          {pointInTime.format('MMM DD, YYYY - HH:mm')} -{' '}
          {pointInTime.clone().add(1, 'h').format('HH:mm')}
        </div>
        <IconButton
          onClick={() => setPointInTime(pointInTime.clone().add(1, 'h'))}
        >
          <ArrowRight size={16} />
        </IconButton>
      </FlexContainer>
      {loading || !data ? (
        <Loader height={500} />
      ) : (
        data.map((d) => {
          return (
            <OrgItem key={d.org.id}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {d.org.data.name}
                  </Typography>

                  {d.templates.map((x) => {
                    return (
                      <div key={x.tpl.id}>
                        <Typography variant="h5" gutterBottom>
                          {x.tpl.data.adminTitle || x.tpl.data.title} - (
                          {sum(
                            flatten(
                              x.experiences.map((e) =>
                                e.schedulesByType.map(
                                  (s) =>
                                    s.ss.filter(
                                      (x) =>
                                        x.data.type !== ScheduleType.TASK_END
                                    ).length
                                )
                              )
                            )
                          )}
                          )
                        </Typography>
                        <br />

                        {x.experiences.map((e) => {
                          return (
                            <ExperienceItem key={e.exp.id}>
                              <ExperienceItemHeader
                                exp={e.exp}
                                tpl={x.tpl}
                                participant={e.participant}
                                org={d.org}
                              />
                              {e.schedulesByType.map((byType) => {
                                const { type, ss } = byType;
                                return (
                                  <SchedulesByType
                                    key={type}
                                    tpl={x.tpl}
                                    exp={e.exp}
                                    type={type}
                                    ss={ss}
                                  />
                                );
                              })}
                            </ExperienceItem>
                          );
                        })}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </OrgItem>
          );
        })
      )}
    </Section>
  );
};
